import { Copy } from 'lucide-react';
import { Button } from '../../ui/button.tsx';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../ui/card.tsx';
import { Input } from '../../ui/input.tsx';
import { Label } from '../../ui/label.tsx';
import {  useEffect, useState } from 'react';
import { useBranding } from '../../../contexts/BrandingContext.tsx';
import { toast } from 'sonner';
import SaveBotton from './SaveBotton.tsx';
import { BrandingSettings } from '@/types/branding.ts';

interface ColorsProps {
  colors: BrandingSettings['colors'];
  setColors: (colors: BrandingSettings['colors']) => void;
}

function Colors({ colors, setColors }: ColorsProps) {
  const { branding, isLoading, isWhiteLabel, updateBranding } = useBranding();
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setColors(branding.colors);
    setHasChanges(false); // Reset changes when branding loads
  }, [branding.colors]);
  
  const handleColorChange = (colorKey: string, value: string) => {
    const newColors = {
      ...colors,
      [colorKey]: value
    };
    
    setColors(newColors);
    setHasChanges(true); // Mark as changed
  };

  const copyColorCode = (color: string) => {
    navigator.clipboard.writeText(color);
    toast.success(`Copied ${color} to clipboard`);
  };

  const handleSave = async () => {
    if (!isWhiteLabel) return toast.error('Only white-label partners can customize branding');
    const updatedBranding = {...branding, colors};
    await updateBranding(updatedBranding);
    setHasChanges(false); // Reset changes after successful save
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Primary Color */}
        <div className="space-y-2">
          <Label className="flex items-center justify-between">
            Primary Color
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyColorCode(colors.primary)}
              className="h-6 px-2"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </Label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={colors.primary}
              onChange={(e) => handleColorChange('primary', e.target.value)}
              className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              value={colors.primary}
              onChange={(e) => handleColorChange('primary', e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Main brand color used for buttons and links
          </p>
        </div>

        {/* Secondary Color */}
        <div className="space-y-2">
          <Label className="flex items-center justify-between">
            Secondary Color
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyColorCode(colors.secondary)}
              className="h-6 px-2"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </Label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={colors.secondary}
              onChange={(e) => handleColorChange('secondary', e.target.value)}
              className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              value={colors.secondary}
              onChange={(e) => handleColorChange('secondary', e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Supporting color for accents and highlights
          </p>
        </div>

        {/* Accent Color */}
        <div className="space-y-2">
          <Label className="flex items-center justify-between">
            Accent Color
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyColorCode(colors.accent)}
              className="h-6 px-2"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </Label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={colors.accent}
              onChange={(e) => handleColorChange('accent', e.target.value)}
              className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              value={colors.accent}
              onChange={(e) => handleColorChange('accent', e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Highlight color for important elements
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Color Presets</CardTitle>
          <CardDescription>Quick color scheme options</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { name: 'Blue', primary: '#3B82F6', secondary: '#8B5CF6', accent: '#06B6D4' },
              { name: 'Green', primary: '#10B981', secondary: '#34D399', accent: '#6EE7B7' },
              { name: 'Purple', primary: '#8B5CF6', secondary: '#A78BFA', accent: '#C4B5FD' },
              { name: 'Orange', primary: '#F59E0B', secondary: '#FBBF24', accent: '#FCD34D' },
              { name: 'Noir', primary: '#4B5563', secondary: '#9CA3AF', accent: '#F9FAFB' },
              { name: 'Rose', primary: '#F472B6', secondary: '#EC4899', accent: '#FDA4AF' },
              { name: 'Ocean', primary: '#0EA5E9', secondary: '#14B8A6', accent: '#A78BFA' },
              { name: 'Sunset', primary: '#F97316', secondary: '#EF4444', accent: '#F59E0B' },
              { name: 'Mint', primary: '#6EE7B7', secondary: '#A7F3D0', accent: '#34D399' },
              { name: 'Cyberpunk', primary: '#f50057', secondary: '#00e5ff', accent: '#76ff03' },
              { name: 'Earthy', primary: '#6d4c41', secondary: '#a1887f', accent: '#ffab91' },
              { name: 'Royal', primary: '#4a148c', secondary: '#7b1fa2', accent: '#e1bee7' }
            ].map((preset) => (
              <Button
                key={preset.name}
                variant="outline"
                className="h-16 flex flex-col p-2"
                onClick={() => {
                  const newColors = {
                    ...colors,
                    primary: preset.primary,
                    secondary: preset.secondary,
                    accent: preset.accent
                  };
                  
                  setColors(newColors);
                  setHasChanges(true); // Mark as changed when preset is applied
                }}
              >
                <div className="flex gap-1 mb-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: preset.primary }}></div>
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: preset.secondary }}></div>
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: preset.accent }}></div>
                </div>
                <span className="text-xs">{preset.name}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      <SaveBotton handleSave={handleSave} isSaving={isLoading} disabled={!hasChanges} />
    </div>
  )
}

export default Colors